'use client';

import { FC, useEffect, useMemo } from 'react';
import { NoResults } from '@bts-web/core-features/common';
import { TTransfersNextStepStateWithActions } from '../../utils/useTransfersNextStepState.client';
import { TCryptoTransferTranslations } from '../../utils/getCryptoTransferTranslations';
import { TransfersActionBar } from '../Reusable/ActionBar/TransfersActionBar';
import { AssetPickerList } from '../Reusable/AssetPickerList/AssetPickerList';
import { NetworkPickerList } from '../Reusable/NetworkPickerList/NetworkPickerList';
import { AssetAndNetworkPage } from '../Reusable/AssetAndNetworkPage/AssetAndNetworkPage';
import { TransfersLayout } from '../Reusable/TransfersLayout/TransfersLayout';
import { useTransfersAssetsListContext } from '../TransfersAssetListContext/TransfersAssetListContext';
import { AssetsListSkeleton } from '../TransfersParent/TransfersSkeleton';
import {
  TransfersSendStepsIds,
  TransfersSectionVariants,
  TransfersCommonStepsIds,
} from '../../types';
import { ChooseRecipientScreen } from './ChooseRecipientScreen/ChooseRecipientScreen';
import { ChooseAmountScreen } from './ChooseAmountScreen/ChooseAmountScreen';
import { ConfirmationScreen } from './ConfirmationScreen/ConfirmationScreen';
import { TwoFactorScreen } from './TwoFactorScreen/TwoFactorScreen';
import { AddNewWalletScreen } from './AddNewWalletScreen/AddNewWalletScreen';
import { MemoIsMissingScreen } from './MemoIsMissingScreen/MemoIsMissingScreen';
import { WalletOwnerTypeScreen } from './WalletOwnerTypeScreen/WalletOwnerTypeScreen';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { useClientUserInfo } from '../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { TriggerSetupTwoFaModal } from '../../../common';
import { useClientLocale } from '@bts-web/utils-lokalise';
import { CryptoTransfersSearchBar } from '../CryptoTransfersSearchBar/CryptoTransfersSearchBar';

export interface SendScreenProps {
  state: TTransfersNextStepStateWithActions;
  transfersTranslations: TCryptoTransferTranslations;
}

const SendScreen: FC<SendScreenProps> = ({ state, transfersTranslations }) => {
  const locale = useClientLocale();

  const { twoFactorAuthentication } = useClientUserInfo();

  const { showModal, closeAll } = useModalDrawerContext();

  const {
    assetsListForSend,
    isAssetsListLoading,
    walletHosts,
    assetFilterSearchValue,
  } = useTransfersAssetsListContext();

  const { pageId, selectedAssetData, bindAssetData, bindNetworkData } = state;

  useEffect(() => {
    if (twoFactorAuthentication !== 'ENABLED') {
      closeAll();

      showModal(TriggerSetupTwoFaModal, {
        disableOverlayClose: true,
      });
    }
  }, [twoFactorAuthentication]);

  const currentNetworkList = useMemo(() => {
    if (!state.selectedAssetData) return null;

    const networkList = assetsListForSend.find(
      (item) => item.id === state.selectedAssetData?.id,
    )?.networkInformation?.networks;

    return networkList ?? null;
  }, [assetsListForSend, state.selectedAssetData]);

  const hasMultipleNetworks = currentNetworkList
    ? currentNetworkList?.length > 1
    : false;

  const showCryptoTransfersSearchBar =
    (assetsListForSend?.length ?? 0) > 10 || !!assetFilterSearchValue;

  const hasAssetsListForSend =
    assetsListForSend && assetsListForSend.length > 0;

  const renderMiddleScrollableRegion = () => {
    if (isAssetsListLoading) {
      return <AssetsListSkeleton />;
    }

    if (!isAssetsListLoading && !hasAssetsListForSend) {
      return <NoResults searchTerm={assetFilterSearchValue} />;
    }

    return pageId === TransfersCommonStepsIds.ChooseAsset ? (
      <AssetPickerList
        onPick={bindAssetData}
        value={selectedAssetData?.id}
        variant={TransfersSectionVariants.Send}
      />
    ) : pageId === TransfersCommonStepsIds.ChooseNetwork &&
      currentNetworkList ? (
      <NetworkPickerList
        locale={locale}
        networkList={currentNetworkList}
        onNetworkSelected={bindNetworkData}
        translations={transfersTranslations}
        state={state}
        variant={TransfersSectionVariants.Send}
      />
    ) : pageId === TransfersCommonStepsIds.AssetAndNetworkSummary ? (
      <AssetAndNetworkPage
        assetList={assetsListForSend}
        state={state}
        variant={TransfersSectionVariants.Send}
        translations={transfersTranslations}
      />
    ) : pageId === TransfersSendStepsIds.ChooseRecipient ? (
      <ChooseRecipientScreen
        state={state}
        translations={transfersTranslations}
      />
    ) : pageId === TransfersSendStepsIds.WalletOwnerType ? (
      <WalletOwnerTypeScreen
        onSelect={(ownerType) => {
          state.setWalletOwnerType(ownerType);

          state.changeToPageAndBindActions({
            pageId: TransfersSendStepsIds.AddNewWallet,
            newBackAction: {
              pageId: TransfersSendStepsIds.WalletOwnerType,
              type: 'page',
            },
          });
        }}
        labels={{
          title: transfersTranslations.walletOwnerTypeQuestion,
          subtitle: transfersTranslations.walletOwnerTypeSubtitle,
          learnMore: transfersTranslations.learnMore,
          personTitle: transfersTranslations.walletOwnerTypePersonTitle,
          personSubtitle: transfersTranslations.walletOwnerTypePersonSubtitle,
          businessTitle: transfersTranslations.walletOwnerTypeBusinessTitle,
          businessSubtitle:
            transfersTranslations.walletOwnerTypeBusinessSubtitle,
        }}
      />
    ) : pageId === TransfersSendStepsIds.AddNewWallet ? (
      <AddNewWalletScreen
        state={state}
        translations={transfersTranslations}
        walletHosts={walletHosts}
      />
    ) : pageId === TransfersSendStepsIds.MemoIsMissing ? (
      <MemoIsMissingScreen state={state} translations={transfersTranslations} />
    ) : pageId === TransfersSendStepsIds.ChooseAmount ? (
      <ChooseAmountScreen
        cryptoTransferState={state}
        translations={transfersTranslations}
      />
    ) : pageId === TransfersSendStepsIds.Confirmation ? (
      <ConfirmationScreen state={state} translations={transfersTranslations} />
    ) : pageId === TransfersSendStepsIds.TwoFactorAuth ? (
      <TwoFactorScreen
        withdrawalId={state?.createdWithdrawalOfferData?.id as string}
        translations={transfersTranslations}
      />
    ) : null;
  };

  return (
    <TransfersLayout
      wrapperProps={{
        'aria-label': 'crypto transfers send flow',
      }}
      topRegion={
        <>
          <TransfersActionBar
            hasMultipleNetworks={hasMultipleNetworks}
            transfersTranslations={transfersTranslations}
            state={state}
          />

          {showCryptoTransfersSearchBar &&
          pageId === TransfersCommonStepsIds.ChooseAsset ? (
            <CryptoTransfersSearchBar
              placeholder={transfersTranslations.searchAssets}
            />
          ) : null}
        </>
      }
      middleScrollableRegion={renderMiddleScrollableRegion()}
    />
  );
};

export { SendScreen };
